import { HttpParams } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { BehaviorSubject, Observable, map } from "rxjs"
import { environment } from "../../environments/environment"
import { ApiService } from "./api.service"
import { AuthService } from "./auth.service"
import { StationContextService } from "./station-context.service"

export interface SupportPermissions {
  can_create: boolean
  can_view_inbox: boolean
  can_view_analytics: boolean
  can_manage_settings: boolean
}

export interface SupportCategory {
  id?: number
  value: string
  label: string
  description: string
  routes_to: "admin" | "station"
  sort_order?: number
  is_active?: number | boolean
}

export interface SupportAttachment {
  id: number
  file_url: string
  file_name: string
  file_size: number
  file_type: string
  ticket_id?: number
  reply_id?: number
}

export interface SupportAssignmentCurrent {
  type?: "station" | "admin" | "user" | string
  label?: string
  station_id?: number | null
  station_name?: string | null
  call_sign?: string | null
  owner_name?: string | null
  assigned_to?: number | null
  assigned_name?: string | null
}

export interface SupportAssignment {
  current?: SupportAssignmentCurrent
  reassigned_at?: string | null
  reassigned_by?: number | null
  reassigned_by_name?: string | null
}

export interface SupportTicket {
  id: number
  ticket_number: string
  user_id: number
  station_id: number | null
  category: string
  subject: string
  description: string
  status: "open" | "in_progress" | "resolved" | "closed"
  priority: "low" | "medium" | "high"
  assigned_to: number | null
  assigned_role?: string | null
  assignee_email?: string | null
  assignee_email_actual?: string | null
  routed_to: "admin" | "station"
  created_at: string
  updated_at: string
  resolved_at: string | null
  reassigned_at?: string | null
  reassigned_by?: number | null
  reassigned_by_firstname?: string
  reassigned_by_lastname?: string
  firstname?: string
  lastname?: string
  user_email?: string
  station_name?: string
  call_sign?: string
  owner_name?: string
  assignee_firstname?: string
  assignee_lastname?: string
  assignment?: SupportAssignment
  reply_count?: number
  attachments?: SupportAttachment[]
  role?: string
  user_role?: string
  submitter_role?: string
  author_role?: string
  effective_role?: string
  source?: "advertiser" | "station" | string
}

export interface SupportReply {
  id: number
  ticket_id: number
  user_id: number
  message: string
  is_internal: boolean
  created_at: string
  firstname: string
  lastname: string
  role: string
  attachments?: SupportAttachment[]
}

export interface TicketsResponse {
  status: string
  tickets: SupportTicket[]
  pagination: {
    total: number
    limit: number
    offset: number
    has_more: boolean
  }
}

export interface SupportAnalytics {
  period: string
  overview: {
    total_tickets: number
    open_count: number
    in_progress_count: number
    resolved_count: number
    closed_count: number
    high_priority_count: number
    avg_resolution_hours: number
    resolution_rate: number
  }
  by_category: Array<{
    category: string
    count: number
    resolved_count: number
  }>
  over_time: Array<{
    date: string
    created: number
    resolved: number
  }>
  by_routing: Array<{
    routed_to: string
    count: number
  }>
  top_stations: Array<{
    station_name: string
    call_sign: string
    ticket_count: number
    open_count: number
  }>
}

export interface ReassignOptions {
  stations: Array<{
    station_id: number
    station_name: string
    call_sign: string
    owner_id: number
    owner_name: string
    station_owners_list: string
    station_owner_ids: string
    owner_user_id?: number
    owner_email?: string
  }>
  admins: Array<{
    id: number
    firstname: string
    lastname: string
    email: string
  }>
}

@Injectable({ providedIn: "root" })
export class SupportService {
  private base = `${environment.apiUrl}/support`
  private permissionsSubject = new BehaviorSubject<SupportPermissions>({
    can_create: false,
    can_view_inbox: false,
    can_view_analytics: false,
    can_manage_settings: false,
  })
  readonly permissions$ = this.permissionsSubject.asObservable()

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private stationContext: StationContextService,
  ) {
    this.permissionsSubject.next(this.permissionsFromRole())
  }

  get permissions(): SupportPermissions {
    return this.permissionsSubject.value
  }

  refreshPermissionsFromRole(): SupportPermissions {
    const next = this.permissionsFromRole()
    this.permissionsSubject.next(next)
    return next
  }

  /**
   * `create` returns only what the caller may submit (staff roles are limited to the
   * admin-routed categories); `filter` returns what is worth filtering a list by.
   */
  getCategories(scope: "create" | "filter" = "create"): Observable<SupportCategory[]> {
    const params = new HttpParams().set("scope", scope)
    return this.apiService.authenticatedGet<unknown>(`${this.base}/categories`, { params }).pipe(
      map((res) => {
        this.capturePermissions(res)
        return this.unwrapCategories(res)
      }),
    )
  }

  /** Admin settings list: all categories, including inactive, with is_active. */
  getSettingsCategories(): Observable<SupportCategory[]> {
    return this.apiService
      .authenticatedGet<unknown>(`${this.base}/settings/categories`)
      .pipe(map((res) => this.unwrapCategories(res)))
  }

  createTicket(data: {
    category: string
    subject: string
    description: string
    reference_type?: "order" | "commercial_request"
    reference_id?: number
    attachment_ids?: number[]
  }): Observable<any> {
    return this.apiService.authenticatedPost(`${this.base}/tickets`, data)
  }

  listTickets(filters?: {
    status?: string
    category?: string
    station_id?: number | string
    owner_id?: number | string
    /** `mine` = tickets the caller raised; `inbox` = incoming tickets to handle. */
    view?: "mine" | "inbox"
    /** Submitter type, filtered server-side by list_support_tickets. */
    source?: "advertiser" | "station"
    limit?: number
    offset?: number
  }): Observable<TicketsResponse> {
    let params = new HttpParams()
    if (filters?.status) params = params.set("status", filters.status)
    if (filters?.category) params = params.set("category", filters.category)
    if (filters?.station_id) params = params.set("station_id", String(filters.station_id))
    if (filters?.owner_id) params = params.set("owner_id", String(filters.owner_id))
    if (filters?.view) params = params.set("view", filters.view)
    if (filters?.source) params = params.set("source", filters.source)
    if (filters?.limit) params = params.set("limit", filters.limit.toString())
    if (filters?.offset !== undefined) params = params.set("offset", filters.offset.toString())
    return this.apiService.authenticatedGet<unknown>(`${this.base}/tickets`, { params }).pipe(
      map((res) => this.unwrapTickets(res)),
    )
  }

  getTicket(ticketId: number): Observable<{
    ticket: SupportTicket
    replies: SupportReply[]
  }> {
    return this.apiService
      .authenticatedGet<{ ticket?: unknown; replies?: SupportReply[] } | SupportTicket>(
        `${this.base}/tickets/${ticketId}`,
      )
      .pipe(
        map((res) => {
          const obj = (res && typeof res === "object" ? res : {}) as Record<string, unknown>
          const rawTicket = obj["ticket"] ?? res
          const replies = Array.isArray(obj["replies"]) ? (obj["replies"] as SupportReply[]) : []
          return {
            ticket: this.normalizeTicket(rawTicket),
            replies,
          }
        }),
      )
  }

  replyToTicket(
    ticketId: number,
    data: {
      message: string
      is_internal?: boolean
      update_status?: string
      attachment_ids?: number[]
    },
  ): Observable<any> {
    return this.apiService.authenticatedPost(`${this.base}/tickets/${ticketId}/reply`, data)
  }

  updateTicket(
    ticketId: number,
    data: {
      status?: string
      priority?: string
      assigned_to?: number
      station_id?: number
      routed_to?: string
      assignee_email?: string
    },
  ): Observable<any> {
    return this.apiService.authenticatedPut(`${this.base}/tickets/${ticketId}`, data)
  }

  uploadAttachment(file: File): Observable<{
    attachment: SupportAttachment
  }> {
    return new Observable((observer) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64 = (e.target?.result as string).split(",")[1]
        this.apiService
          .authenticatedPost<{ attachment: SupportAttachment }>(`${this.base}/attachments/upload`, {
            file_name: file.name,
            file_type: file.type,
            file_size: file.size,
            file_data: base64,
          })
          .subscribe({
            next: (res) => {
              observer.next(res)
              observer.complete()
            },
            error: (err) => observer.error(err),
          })
      }
      reader.onerror = () => observer.error(reader.error)
      reader.readAsDataURL(file)
    })
  }

  getFilterOptions(): Observable<{
    stations: Array<{ id: number; name: string; call_sign: string }>
    owners: Array<{ id: number; name: string }>
  }> {
    return this.apiService.authenticatedGet<unknown>(`${this.base}/filter-options`).pipe(
      map((res) => {
        const obj = (res && typeof res === "object" ? res : {}) as Record<string, unknown>
        this.capturePermissions(obj)
        const nested = obj["data"] && typeof obj["data"] === "object"
          ? (obj["data"] as Record<string, unknown>)
          : {}
        const rawStations = Array.isArray(obj["stations"])
          ? obj["stations"]
          : Array.isArray(nested["stations"])
            ? nested["stations"]
            : []
        const rawOwners = Array.isArray(obj["owners"])
          ? obj["owners"]
          : Array.isArray(nested["owners"])
            ? nested["owners"]
            : []
        return {
          stations: this.normalizeStations(rawStations),
          owners: this.normalizeOwners(rawOwners),
        }
      }),
    )
  }

  getAnalytics(period = "month"): Observable<{ status: string } & SupportAnalytics> {
    return this.apiService.authenticatedGet(`${this.base}/analytics?period=${period}`)
  }

  getReassignOptions(): Observable<{ status: string } & ReassignOptions> {
    return this.apiService.authenticatedGet(`${this.base}/reassign-options`)
  }

  createCategory(data: {
    value: string
    label: string
    description: string
    routes_to: string
    sort_order: number
  }): Observable<any> {
    return this.apiService.authenticatedPost(`${this.base}/settings/categories`, data)
  }

  updateCategory(id: number, data: any): Observable<any> {
    return this.apiService.authenticatedPut(`${this.base}/settings/categories/${id}`, data)
  }

  deactivateCategory(id: number): Observable<any> {
    return this.apiService.authenticatedDelete(`${this.base}/settings/categories/${id}`)
  }

  /**
   * True when the user is assigned to a station (optionally a specific one). users.role can
   * be "user" while the person is station staff through station_users / owner assignments;
   * the backend then serves them that station's tickets (effective_ticket_role).
   */
  isAssignedToStation(stationId?: number | null): boolean {
    const assignments = this.stationContext?.getAssignments() ?? []
    if (stationId == null) return assignments.length > 0
    return assignments.some((a) => Number(a.station_id) === Number(stationId))
  }

  permissionsFromRole(role?: string): SupportPermissions {
    let normalized = (
      role ||
      this.authService?.getCurrentUserInfo()?.role ||
      ""
    ).toLowerCase()
    if (!role && normalized !== "admin" && this.isAssignedToStation()) {
      normalized = "station_user"
    }
    if (normalized === "admin") {
      return {
        can_create: false,
        can_view_inbox: true,
        can_view_analytics: true,
        can_manage_settings: true,
      }
    }
    if (["owner", "station_owner", "station_user"].includes(normalized)) {
      return {
        can_create: true,
        can_view_inbox: true,
        can_view_analytics: false,
        can_manage_settings: false,
      }
    }
    return {
      can_create: true,
      can_view_inbox: false,
      can_view_analytics: false,
      can_manage_settings: false,
    }
  }

  private capturePermissions(res: unknown): void {
    if (!res || typeof res !== "object") return
    const raw = (res as Record<string, unknown>)["permissions"]
    if (!raw || typeof raw !== "object") return
    const perms = raw as Record<string, unknown>
    const fallback = this.permissionsFromRole()
    this.permissionsSubject.next({
      can_create: typeof perms["can_create"] === "boolean" ? perms["can_create"] : fallback.can_create,
      can_view_inbox: typeof perms["can_view_inbox"] === "boolean" ? perms["can_view_inbox"] : fallback.can_view_inbox,
      can_view_analytics:
        typeof perms["can_view_analytics"] === "boolean" ? perms["can_view_analytics"] : fallback.can_view_analytics,
      can_manage_settings:
        typeof perms["can_manage_settings"] === "boolean" ? perms["can_manage_settings"] : fallback.can_manage_settings,
    })
  }

  private normalizeStations(raw: unknown[]): Array<{ id: number; name: string; call_sign: string }> {
    return raw
      .map((item) => {
        const row = (item && typeof item === "object" ? item : {}) as Record<string, unknown>
        const id = Number(row["id"] ?? row["station_id"] ?? row["stationId"])
        if (!Number.isFinite(id) || id <= 0) return null
        return {
          id,
          name: String(row["name"] ?? row["station_name"] ?? row["stationName"] ?? `Station ${id}`),
          call_sign: String(row["call_sign"] ?? row["callSign"] ?? ""),
        }
      })
      .filter((s): s is { id: number; name: string; call_sign: string } => s != null)
  }

  private normalizeOwners(raw: unknown[]): Array<{ id: number; name: string }> {
    return raw
      .map((item) => {
        const row = (item && typeof item === "object" ? item : {}) as Record<string, unknown>
        const id = Number(row["id"] ?? row["owner_id"] ?? row["ownerId"])
        if (!Number.isFinite(id) || id <= 0) return null
        const name = String(
          row["name"] ??
            [row["firstname"], row["lastname"]].filter(Boolean).join(" ") ??
            `Owner ${id}`,
        )
        return { id, name }
      })
      .filter((o): o is { id: number; name: string } => o != null)
  }

  private unwrapCategories(res: unknown): SupportCategory[] {
    let raw: unknown[] = []
    if (Array.isArray(res)) raw = res
    else if (res && typeof res === "object") {
      const obj = res as Record<string, unknown>
      if (Array.isArray(obj["categories"])) raw = obj["categories"]
      else if (Array.isArray(obj["data"])) raw = obj["data"]
    }
    return raw.map((item) => this.normalizeCategory(item))
  }

  normalizeCategory(raw: unknown): SupportCategory {
    const row = (raw && typeof raw === "object" ? raw : {}) as SupportCategory & Record<string, unknown>
    return {
      ...row,
      is_active: this.toBooleanFlag(row["is_active"]),
    }
  }

  toBooleanFlag(value: unknown): boolean {
    return value === true || value === 1 || value === "1" || value === "true"
  }

  private unwrapTickets(res: unknown): TicketsResponse {
    if (Array.isArray(res)) {
      const tickets = (res as unknown[]).map((t) => this.normalizeTicket(t))
      return {
        status: "success",
        tickets,
        pagination: {
          total: tickets.length,
          limit: tickets.length,
          offset: 0,
          has_more: false,
        },
      }
    }

    const obj = (res && typeof res === "object" ? res : {}) as Record<string, unknown>
    const rawTickets = Array.isArray(obj["tickets"]) ? obj["tickets"] : []
    const tickets = rawTickets.map((t) => this.normalizeTicket(t))
    const pagination = (obj["pagination"] && typeof obj["pagination"] === "object"
      ? obj["pagination"]
      : {}) as Record<string, unknown>

    return {
      status: typeof obj["status"] === "string" ? obj["status"] : "success",
      tickets,
      pagination: {
        total: Number(pagination["total"] ?? tickets.length),
        limit: Number(pagination["limit"] ?? tickets.length),
        offset: Number(pagination["offset"] ?? 0),
        has_more: Boolean(pagination["has_more"]),
      },
    }
  }

  /** Pulls the submitter role off a ticket row, including nested user objects. */
  extractSubmitterRole(ticket: unknown): string {
    const row = (ticket && typeof ticket === "object" ? ticket : {}) as Record<string, unknown>
    const nestedUser = (row["user"] && typeof row["user"] === "object"
      ? row["user"]
      : {}) as Record<string, unknown>
    const nestedSubmitter = (row["submitter"] && typeof row["submitter"] === "object"
      ? row["submitter"]
      : {}) as Record<string, unknown>
    const candidates = [
      row["role"],
      row["user_role"],
      row["submitter_role"],
      row["author_role"],
      row["effective_role"],
      row["created_by_role"],
      nestedUser["role"],
      nestedUser["effective_role"],
      nestedSubmitter["role"],
      nestedSubmitter["effective_role"],
    ]
    for (const value of candidates) {
      if (typeof value === "string" && value.trim()) return value.trim().toLowerCase()
    }
    return ""
  }

  ticketSource(ticket: unknown): "advertiser" | "station" {
    const role = this.extractSubmitterRole(ticket)
    if (["owner", "station_owner", "station_user"].includes(role)) return "station"
    if (role === "user" || role === "advertiser") return "advertiser"
    const row = (ticket && typeof ticket === "object" ? ticket : {}) as Record<string, unknown>
    const explicit = String(row["source"] || "").toLowerCase()
    if (explicit === "station" || explicit === "advertiser") return explicit
    const stationId = row["station_id"]
    const routedTo = String(row["routed_to"] || "").toLowerCase()
    if (stationId && routedTo === "admin") return "station"
    return "advertiser"
  }

  ticketSourceLabel(ticket: unknown): string {
    return this.ticketSource(ticket) === "station" ? "Station staff" : "Advertiser"
  }

  private normalizeTicket(raw: unknown): SupportTicket {
    const row = (raw && typeof raw === "object" ? raw : {}) as SupportTicket & Record<string, unknown>
    const role = this.extractSubmitterRole(row)
    return {
      ...(row as SupportTicket),
      role: role || row.role,
      user_role: role || row.user_role,
    }
  }
}
